import { ApiClient } from '../lib/api';
import {
  Event,
  EventSchema,
  CreateEvent,
  CreateEventSchema,
  UpdateEvent,
  UpdateEventSchema
} from '../models/event';
import { z } from 'zod';

export class EventService {
  private readonly api: ApiClient;

  constructor(baseURL: string, stage: string) {
    this.api = new ApiClient(`${baseURL}/${stage}/events-service-${stage}`, stage);
  }

  private setDefaults(data: unknown): Event {
    return EventSchema.parse(data);
  }

  async getAllEvents(): Promise<Event[]> {
    const events = await this.api.request(
      { method: 'GET', url: '/events' },
      EventSchema.array()
    );
    return events.map(event => this.setDefaults(event));
  }

  async getAllActiveEvents(): Promise<Event[]> {
    const events = await this.api.request(
      { method: 'GET', url: '/events?onlyActive=true' },
      EventSchema.array()
    );
    return events.map(event => this.setDefaults(event));
  }

  async getEvents(startDate: string, endDate: string): Promise<Event[]> {
    const events = await this.api.request(
      { method: 'GET', url: `/events?startDate=${startDate}&endDate=${endDate}` },
      EventSchema.array()
    );
    return events.map(event => this.setDefaults(event));
  }

  async getActiveEvents(startDate: string, endDate: string): Promise<Event[]> {
    const events = await this.api.request(
      { method: 'GET', url: `/events?onlyActive=true&startDate=${startDate}&endDate=${endDate}` },
      EventSchema.array()
    );
    return events.map(event => this.setDefaults(event));
  }

  async getEventById(id: number): Promise<Event> {
    const event = await this.api.request(
      { method: 'GET', url: `/events/${id}` },
      EventSchema
    );
    return this.setDefaults(event);
  }

  async getEventsByUserId(euid: number): Promise<Event[]> {
    const events = await this.api.request(
      { method: 'GET', url: `/events/user/${euid}` },
      EventSchema.array()
    );
    return events.map(event => this.setDefaults(event));
  }

  async createEvent(data: CreateEvent): Promise<Event> {
    const event = await this.api.request(
      {
        method: 'POST',
        url: '/events',
        data: CreateEventSchema.parse(data)
      },
      EventSchema
    );
    return this.setDefaults(event);
  }

  async updateEvent(data: UpdateEvent): Promise<Event> {
    const event = await this.api.request(
      {
        method: 'PUT',
        url: '/events',
        data: UpdateEventSchema.parse(data)
      },
      EventSchema
    );
    return this.setDefaults(event);
  }

  async deleteEvent(id: number): Promise<void> {
    await this.api.request(
      { method: 'DELETE', url: `/events/${id}` },
      z.object({
        message: z.string()
      })
    );
  }
} 